import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, CalendarDays, RefreshCw, Loader2, CalendarIcon, Clock, History, ChevronRight, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, startOfWeek, addWeeks, parseISO } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MenusPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organizedMenus, setOrganizedMenus] = useState({ current: null, upcoming: [], history: [] });
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    week_start: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    avoid_consecutive_category: true,
    avoid_consecutive_recipe: true,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [menusRes, recipesRes] = await Promise.all([
        fetch(`${API}/menus/organized`, { credentials: 'include' }),
        fetch(`${API}/recipes`, { credentials: 'include' }),
      ]);

      if (menusRes.ok) {
        const data = await menusRes.json();
        setOrganizedMenus(data);
      }
      if (recipesRes.ok) {
        const data = await recipesRes.json();
        setRecipes(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // MOCK DATA FOR TUTORIAL
  const MOCK_CURRENT_MENU = {
    menu_id: 'demo',
    title: 'Demo Menu',
    week_start: new Date().toISOString().split('T')[0],
    week_end: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    total_calories: 14500,
  };

  const { current, upcoming, history } = organizedMenus;

  // Calculate showDemo based on empty data and tutorial status
  // Calculate showDemo based on empty data and tutorial status
  // user is already destructured from useAuth() at top of component scope
  const showDemo = !current && upcoming.length === 0 && history.length === 0 && !loading && user && !user.tutorial_pages_seen?.includes('menus_list');

  const displayCurrentMenu = showDemo ? MOCK_CURRENT_MENU : current;
  const hasNoMenus = !displayCurrentMenu && upcoming.length === 0 && history.length === 0;

  const generateMenu = async () => {
    if (recipes.length === 0) {
      toast.error(t('errors.noRecipes'));
      navigate('/recipes/new');
      return;
    }

    // Check if we have recipes in all categories
    const hasLunch = recipes.some(r => r.categories?.includes('lunch') || r.category === 'lunch');
    const hasDinnerMain = recipes.some(r => r.categories?.includes('dinner_main') || r.category === 'dinner_main');
    const hasDinnerSecondary = recipes.some(r => r.categories?.includes('dinner_secondary') || r.category === 'dinner_secondary');

    if (!hasLunch || !hasDinnerMain || !hasDinnerSecondary) {
      toast.error(language === 'es'
        ? 'Necesitas al menos una receta de cada categoría (almuerzo, cena principal, cena secundaria)'
        : 'You need at least one recipe from each category (lunch, dinner main, dinner side)');
      return;
    }

    if (!formData.title.trim()) {
      toast.error(language === 'es' ? 'El título es requerido' : 'Title is required');
      return;
    }

    setGenerating(true);

    try {
      const response = await fetch(`${API}/menus/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          week_start: formData.week_start,
          avoid_consecutive_category: formData.avoid_consecutive_category,
          avoid_consecutive_recipe: formData.avoid_consecutive_recipe,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(t('success.saved'));
        navigate(`/menu/${data.menu_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error generating menu:', error);
      toast.error(t('errors.generic'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteMenu = async () => {
    if (!menuToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API}/menus/${menuToDelete.menu_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(t('success.deleted'));
        // Refresh data
        fetchData();
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      toast.error(t('errors.generic'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setMenuToDelete(null);
    }
  };

  const dateLocale = language === 'es' ? es : enUS;

  // Filter only Mondays for calendar
  const isMonday = (date) => date.getDay() === 1;

  // Get the start of current week (Monday)
  const currentWeekMonday = startOfWeek(new Date(), { weekStartsOn: 1 });

  // Format date range for display
  const formatDateRange = (weekStart, weekEnd) => {
    if (!weekStart || !weekEnd) return '';
    const start = typeof weekStart === 'string' ? parseISO(weekStart) : weekStart;
    const end = typeof weekEnd === 'string' ? parseISO(weekEnd) : weekEnd;
    return `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }



  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h1 className="font-manrope font-bold text-3xl text-charcoal">
            {t('menu.title')}
          </h1>
          <Button
            data-testid="new-menu-button"
            onClick={() => setShowForm(!showForm)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 font-manrope font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t('menu.generate')}
          </Button>
        </div>

        {/* Generate Form */}
        {showForm && (
          <Card className="bg-white rounded-3xl shadow-soft p-6 md:p-8 mb-8 animate-fade-in" data-testid="menu-generate-form">
            <h2 className="font-manrope font-semibold text-xl text-charcoal mb-6">
              {language === 'es' ? 'Crear nuevo menú' : 'Create new menu'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Title */}
              <div className="space-y-2">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('menu.form.title')} *
                </Label>
                <Input
                  data-testid="menu-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('menu.form.titlePlaceholder')}
                  className="h-12 rounded-xl border-border bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary font-inter"
                />
              </div>

              {/* Week Start */}
              <div className="space-y-2">
                <Label className="font-manrope font-semibold text-charcoal">
                  {t('menu.form.weekStart')}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start rounded-xl border-border bg-white/50 font-inter"
                      data-testid="menu-week-start-button"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 text-stone" strokeWidth={1.5} />
                      {format(new Date(formData.week_start), 'PPP', { locale: dateLocale })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={new Date(formData.week_start)}
                      onSelect={(date) => {
                        if (date) {
                          // Adjust to Monday if not already
                          const monday = startOfWeek(date, { weekStartsOn: 1 });
                          setFormData({ ...formData, week_start: format(monday, 'yyyy-MM-dd') });
                        }
                      }}
                      locale={dateLocale}
                      disabled={(date) => {
                        // Disable days before current week Monday and non-Mondays
                        return date < currentWeekMonday || !isMonday(date);
                      }}
                      modifiers={{
                        monday: isMonday
                      }}
                      modifiersStyles={{
                        monday: { fontWeight: 'bold' }
                      }}
                    />
                    <p className="text-xs text-stone text-center pb-3 px-3">
                      {language === 'es' ? 'Solo puedes seleccionar lunes (inicio de semana)' : 'You can only select Mondays (week start)'}
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Generation options */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <Checkbox
                  checked={formData.avoid_consecutive_category}
                  onCheckedChange={(checked) => setFormData({ ...formData, avoid_consecutive_category: !!checked })}
                  className="mt-0.5"
                />
                <div>
                  <p className="font-manrope font-semibold text-sm text-charcoal">
                    {language === 'es' ? 'Evitar mismo tipo de alimento seguido' : 'Avoid same food type on consecutive days'}
                  </p>
                  <p className="font-inter text-xs text-stone">
                    {language === 'es'
                      ? 'No pondrá la misma categoría dos días seguidos en el mismo tipo de comida.'
                      : "Won't place the same food category two days in a row for the same meal."}
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <Checkbox
                  checked={formData.avoid_consecutive_recipe}
                  onCheckedChange={(checked) => setFormData({ ...formData, avoid_consecutive_recipe: !!checked })}
                  className="mt-0.5"
                />
                <div>
                  <p className="font-manrope font-semibold text-sm text-charcoal">
                    {language === 'es' ? 'Evitar misma receta seguida' : 'Avoid same recipe on consecutive days'}
                  </p>
                  <p className="font-inter text-xs text-stone">
                    {language === 'es'
                      ? 'No repetirá la misma receta dos días seguidos.'
                      : "Won't repeat the same recipe two days in a row."}
                  </p>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowForm(false)}
                className="rounded-full px-6"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={generateMenu}
                disabled={generating || recipes.length === 0}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-5 font-manrope font-bold"
                data-testid="generate-menu-button"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('menu.generating')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    {t('menu.generate')}
                  </>
                )}
              </Button>
            </div>

            {recipes.length === 0 && (
              <p className="text-center text-stone mt-4 font-inter">
                {t('errors.noRecipes')}
                <Button variant="link" onClick={() => navigate('/recipes/new')} className="text-primary">
                  {language === 'es' ? 'Crear receta' : 'Create recipe'}
                </Button>
              </p>
            )}
          </Card>
        )}

        {/* No Menus State */}
        {hasNoMenus && !showForm && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarDays className="w-10 h-10 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="font-manrope font-semibold text-xl text-charcoal mb-2">
              {t('dashboard.noMenus')}
            </h3>
            <p className="font-inter text-stone mb-6">
              {t('dashboard.noMenusHint')}
            </p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 font-manrope font-bold"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('dashboard.generateMenu')}
            </Button>
          </div>
        )}

        {/* Current Week Menu */}
        {displayCurrentMenu && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-manrope font-semibold text-lg text-charcoal">
                {language === 'es' ? 'Menú de esta semana' : "This week's menu"}
              </h2>
            </div>
            <Card
              className={`bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-2xl p-5 transition-all group ${showDemo ? 'opacity-90' : 'cursor-pointer hover:shadow-lift'}`}
              onClick={() => !showDemo && navigate(`/menu/${displayCurrentMenu.menu_id}`)}
              data-testid="current-week-menu"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-manrope font-semibold text-lg text-charcoal mb-1">
                    {displayCurrentMenu.title}
                  </h3>
                  <p className="font-inter text-sm text-stone">
                    {formatDateRange(displayCurrentMenu.week_start, displayCurrentMenu.week_end)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    {displayCurrentMenu.total_calories && (
                      <p className="font-inter text-sm text-sage">
                        {displayCurrentMenu.total_calories} kcal
                      </p>
                    )}
                  </div>
                  {!showDemo && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-stone hover:text-destructive hover:bg-destructive/10 rounded-full z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuToDelete(displayCurrentMenu);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  )}
                  <ChevronRight className={`w-5 h-5 text-primary transition-transform ${showDemo ? 'opacity-50' : 'group-hover:translate-x-1'}`} strokeWidth={1.5} />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Upcoming Menus */}
        {upcoming.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-sage" strokeWidth={1.5} />
              <h2 className="font-manrope font-semibold text-lg text-charcoal">
                {language === 'es' ? 'Menús próximos' : 'Upcoming menus'}
              </h2>
              <span className="text-xs bg-sage/20 text-sage px-2 py-0.5 rounded-full font-inter">
                {upcoming.length}
              </span>
            </div>
            <div className="space-y-3">
              {upcoming.map((menu, index) => (
                <Card
                  key={menu.menu_id}
                  className="bg-white rounded-2xl shadow-soft p-4 cursor-pointer hover:shadow-lift transition-all animate-fade-in group"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => navigate(`/menu/${menu.menu_id}`)}
                  data-testid={`upcoming-menu-${menu.menu_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-manrope font-medium text-charcoal mb-1">
                        {menu.title}
                      </h3>
                      <p className="font-inter text-sm text-stone">
                        {formatDateRange(menu.week_start, menu.week_end)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {menu.total_calories && (
                        <span className="font-inter text-sm text-sage">
                          {menu.total_calories} kcal
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-stone hover:text-destructive hover:bg-destructive/10 rounded-full z-10 h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuToDelete(menu);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <ChevronRight className="w-4 h-4 text-stone group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-stone" strokeWidth={1.5} />
              <h2 className="font-manrope font-semibold text-lg text-charcoal">
                {language === 'es' ? 'Historial' : 'History'}
              </h2>
              <span className="text-xs bg-muted text-stone px-2 py-0.5 rounded-full font-inter">
                {history.length}
              </span>
            </div>
            <div className="space-y-3">
              {history.slice(0, 5).map((menu, index) => (
                <Card
                  key={menu.menu_id}
                  className="bg-muted/30 rounded-2xl p-4 cursor-pointer hover:bg-muted/50 transition-all animate-fade-in group"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => navigate(`/menu/${menu.menu_id}`)}
                  data-testid={`history-menu-${menu.menu_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-manrope font-medium text-charcoal/70 mb-1">
                        {menu.title}
                      </h3>
                      <p className="font-inter text-sm text-stone">
                        {formatDateRange(menu.week_start, menu.week_end)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {menu.total_calories && (
                        <span className="font-inter text-sm text-stone/70">
                          {menu.total_calories} kcal
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-stone/50 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                    </div>
                  </div>
                </Card>
              ))}

              {history.length > 5 && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/history')}
                  className="w-full text-stone hover:text-charcoal"
                >
                  {language === 'es' ? 'Ver todo el historial' : 'View all history'} ({history.length})
                </Button>
              )}
            </div>
          </div>
        )}
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {t('delete')} "{menuToDelete?.title}"
              </DialogTitle>
            </DialogHeader>
            <p className="font-inter text-stone py-4">
              {language === 'es'
                ? '¿Estás seguro de que quieres eliminar este menú? Esta acción no se puede deshacer.'
                : 'Are you sure you want to delete this menu? This action cannot be undone.'}
            </p>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteMenu}
                disabled={deleting}
                className="rounded-full"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {t('delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
